import { tiktok, TiktokAudio } from '@round/api';
import { CampaignTableRow } from './types';
import { TableProps } from '@round/ui-kit';
import { ColumnDef } from '@tanstack/react-table';
import { CampaignsTableMeta } from '../../../Campaigns/types';
import ProjectCell, {
    Meta as ProjectCellMeta,
    ProjectData,
} from '../../../Campaigns/components/cells/ProjectCell/ProjectCell';
import ExpandCell from '../../../Campaigns/components/cells/ExpandCell/ExpandCell';
import styles from './PublicReportCampaignTable.module.css';
import ReportCampaignCell from '../../../Campaigns/components/CampaignsTable/components/ReportCampaignCell/ReportCampaignCell';
import { toDecimalPoint } from '@round/utils';
import { buildTiktokMusicUrl, numberWithCommas } from 'helpers';
import CampaignStatsCell from '../../../Campaigns/components/cells/CampaignStatsCell';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from '../../../helpers';
import DurationCell from '../../../Campaigns/components/cells/DurationCell';
import CampaignsTable from '../../../Campaigns/components/CampaignsTable/CampaignsTable';
import AudioPostGrowthCell, {
    Meta as AudioPostGrowthCellMeta,
} from 'Modules/Plans/Campaigns/components/cells/AudioPostGrowthCell';
import PostsCell from './components/PostsCell';
import ProgressCell from './components/ProgressCell';
import CampaignCostCell from './components/CampaignCostCell';
import TikTokActionsCell from './components/ActionsCell/TikTokActionsCell';

export type TikTokPublicReportCampaignTableRow = CampaignTableRow & {
    audioTimeSeries?: tiktok.TiktokTimeSeriesResponse<'posts_daily_change'>[number] | null;
    audio?: TiktokAudio | null;
};

type Props = Pick<
    TableProps<TikTokPublicReportCampaignTableRow>,
    'data' | 'expandedState' | 'onExpandedChange' | 'renderSubComponent'
> & {
    hasError?: boolean;
    isLoading: boolean;
    meta: Meta;
};

export type Meta = CampaignsTableMeta &
    AudioPostGrowthCellMeta<TikTokPublicReportCampaignTableRow> &
    Pick<
        ProjectCellMeta<TikTokPublicReportCampaignTableRow>,
        'getIsBrandLoading' | 'getIsProjectLoading' | 'getIsTeamLoading'
    >;

const TikTokPublicReportCampaignTable = ({
    data,
    isLoading,
    hasError,
    expandedState,
    onExpandedChange,
    renderSubComponent,
    meta,
}: Props) => {
    const columns: ColumnDef<TikTokPublicReportCampaignTableRow, any>[] = [
        {
            header: '',
            id: 'expand',
            cell: ExpandCell,
        },
        {
            header: 'Project',
            id: 'project',
            accessorFn: (row): ProjectData => ({
                name: row.project?.name ?? '-',
                image: row.project?.image,
                brandImage: row.brand?.image,
                brandName: row.brand?.name ?? '-',
                teamName: row.team?.name ?? '-',
            }),
            cell: ProjectCell,
        },
        {
            header: 'Campaign',
            accessorKey: 'name',
            meta: {
                className: styles.campaignColumn,
            },
            cell: ReportCampaignCell,
        },
        {
            header: 'Posts',
            id: 'posts',
            cell: PostsCell,
        },
        {
            header: 'Progress',
            id: 'progress',
            cell: ProgressCell,
        },
        {
            header: 'Cost',
            id: 'cost',
            cell: CampaignCostCell,
        },
        {
            header: 'Views',
            id: 'views',
            accessorFn: (row) => (row.stats?.view_count ? numberWithCommas(row.stats.view_count) : '-'),
            cell: CampaignStatsCell,
        },
        {
            header: () => (
                <TooltipHeader header="ER" tooltipTitle="Engagement Rate" tooltipBody={tooltipCopy.engagementRate} />
            ),
            id: 'er',
            accessorFn: (row) =>
                typeof row.stats?.engagement_rate === 'number'
                    ? `${toDecimalPoint(row.stats.engagement_rate * 100, 2)}%`
                    : '-',
            cell: CampaignStatsCell,
        },
        {
            header: 'Duration',
            id: 'duration',
            cell: DurationCell,
        },
        {
            header: 'Audio',
            id: 'audio',
            accessorFn: (row) => ({
                timeSeries: row.audioTimeSeries,
                audioUrl: row.audio ? buildTiktokMusicUrl(row.audio.title, row.audio.tiktok_id) : undefined,
            }),
            meta: { className: styles.audioGraph },
            cell: AudioPostGrowthCell,
        },
        {
            header: '',
            id: 'actions',
            meta: { className: styles.actionsCell },
            cell: TikTokActionsCell,
        },
    ];

    return (
        <CampaignsTable
            className={styles.table}
            data={data}
            columns={columns}
            meta={meta}
            isLoading={isLoading}
            hasError={hasError}
            expandedState={expandedState}
            onExpandedChange={onExpandedChange}
            onRowClick={(row) => {
                if (typeof expandedState === 'object') {
                    onExpandedChange?.({ ...expandedState, [row.id]: !expandedState[row.id] });
                }
            }}
            renderSubComponent={renderSubComponent}
            columnVisibility={{
                cost: data.some((d) => 'cost' in d),
                audio: data.some((d) => d.audioTimeSeries),
            }}
        />
    );
};

export default TikTokPublicReportCampaignTable;
